import { useMemo } from "react";
import type { GroupedTableFlatRowProps } from "./GroupedTableFlatRow.types";
import { useGroupedTableInternal } from "../utils";

/**
 * GroupedTableFlatRow component
 *
 * @param {GroupedTableFlatRowProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function GroupedTableFlatRow<TRow>({ item }: GroupedTableFlatRowProps): JSX.Element | null {
    const {
        api,
        columns,
        indentSize,
        isExpanded,
        getGroup,
        getRow,
        slots,
        onRowClick,
    } = useGroupedTableInternal<TRow>();

    const indentPx = useMemo(() => {
        switch (item.kind) {
            case "group": {
                const groupNode = getGroup(item.id);

                return (groupNode?.depth ?? 0) * indentSize;
            }

            case "row":    
            case "loading":
            case "error": {
                const parentGroup = getGroup(item.parentGroupId);
                
                return ((parentGroup?.depth ?? 0) + 1) * indentSize;
            }
        }
    }, [getGroup, item, indentSize]);

    const renderSubcomponent = () => {
        switch (item.kind) {
            case "error": {
                if (!slots?.ErrorRow) {
                    return null;
                }

                const parentGroup = getGroup(item.parentGroupId);
                if (!parentGroup) {
                    return null;
                }

                return <slots.ErrorRow
                    indentPx={indentPx}
                    message={item.message}
                    parentGroup={parentGroup} />;
            }

            case "group":
                if (!slots?.GroupRow) {
                    return null;
                }

                return <slots.GroupRow
                    columns={columns}
                    indentPx={indentPx}
                    group={getGroup(item.id)!}
                    isExpanded={isExpanded(item.id)}
                    toggle={() => api.toggleGroup(item.id)} />;

            case "loading": {
                if (!slots?.LoadingRow) {
                    return null;
                }

                const parent = getGroup(item.parentGroupId);
                if (!parent) {
                    return null;
                }

                return <slots.LoadingRow
                    indentPx={indentPx}
                    parentGroup={parent} />;
            }

            case "row": {
                if (!slots?.DataRow) {
                    return null;
                }

                const row = getRow(item.id);
                if (!row) {
                    return null;
                }

                const onClick = !onRowClick
                    ? undefined
                    : () => onRowClick(
                        row.data,
                        { rowId: row.id, parentGroupId: row.parentGroupId });

                return <slots.DataRow
                    columns={columns}
                    indentPx={indentPx}
                    onClick={onClick}
                    row={row} />;
            }
        }
    };

    return (
        <div className="GroupedTableFlatRow">
            {renderSubcomponent()}
        </div>
    )
}

export default GroupedTableFlatRow;

